/**
 * Parsing and grouping for release-please changelog bodies.
 *
 * `CHANGELOG.md` entries are written for contributors, not for the people who
 * read the in-app releases surfaces: every line ends in `([#123](…)) ([sha](…))`
 * references into a private repo (dead links for readers) and starts with a raw
 * commit scope (`**sandbox-filesystem:**`, `**deps:**`). This module turns that
 * raw markdown into structured entries — reference links stripped, scope lifted
 * out and mapped to a human product area — so the UI can group changes by area
 * instead of repeating a scope token on every row.
 */

/** A single changelog bullet with its scope resolved to a product area. */
export interface ChangelogEntry {
  /** Raw conventional-commit scope (`web`, `sandbox-filesystem`), if any. */
  scope?: string;
  /** Human product area the entry is grouped under. */
  area: string;
  /** Entry text: reference links removed, scope prefix removed. */
  text: string;
}

/** Entries sharing one product area, in the order they should render. */
export interface ChangelogAreaGroup {
  area: string;
  entries: ChangelogEntry[];
}

/** Area used for entries with no scope at all. Always sorts last. */
export const CHANGELOG_GENERAL_AREA = 'General';

/**
 * Display order for the curated areas. Areas resolved by the fallback (an
 * unmapped scope, prettified) sort after these, alphabetically, and
 * {@link CHANGELOG_GENERAL_AREA} always sorts last.
 */
export const CHANGELOG_AREA_ORDER = [
  'Agents',
  'Chat',
  'Automations',
  'Integrations',
  'Sandboxes',
  'Workspace & access',
  'Interface',
  'Docs & learning',
  'Platform',
] as const;

/**
 * Commit scope -> product area. Only scopes that actually appear in
 * `CHANGELOG.md` are listed; anything else falls back to a prettified version
 * of the scope itself (see {@link changelogAreaLabel}), so a new scope shows up
 * as its own readable group rather than disappearing into an "Other" bucket.
 */
const SCOPE_AREAS: Record<string, string> = {
  // Agents
  agent: 'Agents',
  'agent-claude': 'Agents',
  'agent-codex': 'Agents',
  'agent-detail': 'Agents',
  'agent-opencode': 'Agents',
  agents: 'Agents',
  acp: 'Agents',
  'ask-user-question': 'Agents',
  claude: 'Agents',
  'claude-max': 'Agents',
  codex: 'Agents',
  'computer-use': 'Agents',
  crew: 'Agents',
  'cursor-agent': 'Agents',
  groups: 'Agents',
  launch: 'Agents',
  'launch-dialog': 'Agents',
  'launch-manifest': 'Agents',
  launcher: 'Agents',
  'model-catalogs': 'Agents',
  'model-providers': 'Agents',
  models: 'Agents',
  oc: 'Agents',
  opencode: 'Agents',
  persona: 'Agents',
  personas: 'Agents',
  'plugin-agent-claude': 'Agents',
  'plugin-agent-persona': 'Agents',
  'plugin-crew': 'Agents',
  skills: 'Agents',

  // Chat
  chat: 'Chat',
  chief: 'Chat',
  'plugin-chat': 'Chat',
  replay: 'Chat',
  stream: 'Chat',

  // Automations
  automations: 'Automations',
  'axon-viewer': 'Automations',
  events: 'Automations',
  flows: 'Automations',
  gateway: 'Automations',
  mcp: 'Automations',
  'plugin-axon-viewer': 'Automations',
  'plugin-flows': 'Automations',
  'plugin-mcp': 'Automations',
  queue: 'Automations',
  webhooks: 'Automations',

  // Integrations
  arcade: 'Integrations',
  ashby: 'Integrations',
  aws: 'Integrations',
  canny: 'Integrations',
  gh: 'Integrations',
  github: 'Integrations',
  'google-workspace': 'Integrations',
  'incident-io': 'Integrations',
  integrations: 'Integrations',
  jira: 'Integrations',
  linear: 'Integrations',
  'link-unfurl': 'Integrations',
  'plugin-github': 'Integrations',
  'plugin-google-workspace': 'Integrations',
  'plugin-hubspot': 'Integrations',
  'plugin-jira': 'Integrations',
  'plugin-linear': 'Integrations',
  'plugin-link-unfurl': 'Integrations',
  'plugin-slack': 'Integrations',
  pylon: 'Integrations',
  sentry: 'Integrations',
  slack: 'Integrations',
  tailscale: 'Integrations',

  // Sandboxes
  'base-blueprint': 'Sandboxes',
  'base-image': 'Sandboxes',
  blueprint: 'Sandboxes',
  blueprints: 'Sandboxes',
  cli: 'Sandboxes',
  'code-server': 'Sandboxes',
  databases: 'Sandboxes',
  'db-explorer': 'Sandboxes',
  devbox: 'Sandboxes',
  docker: 'Sandboxes',
  filesystem: 'Sandboxes',
  'native-editor': 'Sandboxes',
  'network-policy': 'Sandboxes',
  'plugin-network-policy': 'Sandboxes',
  'plugin-sandbox-filesystem': 'Sandboxes',
  sandbox: 'Sandboxes',
  'sandbox-filesystem': 'Sandboxes',
  'sandbox-shell': 'Sandboxes',

  // Workspace & access
  admin: 'Workspace & access',
  auth: 'Workspace & access',
  'auth-slack': 'Workspace & access',
  authz: 'Workspace & access',
  billing: 'Workspace & access',
  company: 'Workspace & access',
  costs: 'Workspace & access',
  credentials: 'Workspace & access',
  'enterprise-identity': 'Workspace & access',
  identity: 'Workspace & access',
  idp: 'Workspace & access',
  impersonation: 'Workspace & access',
  invites: 'Workspace & access',
  onboarding: 'Workspace & access',
  org: 'Workspace & access',
  'org-setup': 'Workspace & access',
  pii: 'Workspace & access',
  product: 'Workspace & access',
  profile: 'Workspace & access',
  project: 'Workspace & access',
  projects: 'Workspace & access',
  rbac: 'Workspace & access',
  referrals: 'Workspace & access',
  secrets: 'Workspace & access',
  seeds: 'Workspace & access',
  'service-accounts': 'Workspace & access',
  setup: 'Workspace & access',
  sharing: 'Workspace & access',
  teams: 'Workspace & access',
  trials: 'Workspace & access',
  users: 'Workspace & access',

  // Interface
  farm: 'Interface',
  feedback: 'Interface',
  filters: 'Interface',
  game: 'Interface',
  inbox: 'Interface',
  leaderboard: 'Interface',
  nav: 'Interface',
  notifications: 'Interface',
  'plugin-notifications': 'Interface',
  themes: 'Interface',
  town: 'Interface',
  ui: 'Interface',
  village: 'Interface',
  web: 'Interface',

  // Docs & learning
  changelog: 'Docs & learning',
  demos: 'Docs & learning',
  docs: 'Docs & learning',
  'guided-demos': 'Docs & learning',
  marketing: 'Docs & learning',
  'plugin-docs': 'Docs & learning',
  'plugin-marketing': 'Docs & learning',
  'plugin-tutorial': 'Docs & learning',
  seo: 'Docs & learning',
  tutorial: 'Docs & learning',
  tutorials: 'Docs & learning',
  videos: 'Docs & learning',

  // Platform
  alerts: 'Platform',
  analytics: 'Platform',
  api: 'Platform',
  benchmarks: 'Platform',
  bridge: 'Platform',
  build: 'Platform',
  cache: 'Platform',
  ci: 'Platform',
  'code-review': 'Platform',
  'connection-supervisor': 'Platform',
  db: 'Platform',
  deploy: 'Platform',
  deps: 'Platform',
  'flex-bridge': 'Platform',
  infra: 'Platform',
  log: 'Platform',
  logger: 'Platform',
  observability: 'Platform',
  ops: 'Platform',
  perf: 'Platform',
  'plugin-api': 'Platform',
  plugins: 'Platform',
  'pr-review': 'Platform',
  protocol: 'Platform',
  realtime: 'Platform',
  reflex: 'Platform',
  release: 'Platform',
  'runloop-flow': 'Platform',
  sdk: 'Platform',
  security: 'Platform',
  server: 'Platform',
  shared: 'Platform',
  smoke: 'Platform',
  telemetry: 'Platform',
  tests: 'Platform',
  tooling: 'Platform',
  turbo: 'Platform',
  utils: 'Platform',
  'work-assessment': 'Platform',
};

/**
 * A trailing release-please reference: `([#123](url))`, `([1fbc6ce](url))`, or
 * a bare `(#123)`. Both bracketed forms are anchored to a `#`-number or a git
 * sha so real inline links in an entry survive. The leading gap is `[ \t]*`
 * rather than `\s*` — an entry is a single line by the time this runs, and
 * `\s*` before a `/g` scan backtracks quadratically on long whitespace runs
 * (CodeQL js/polynomial-redos), the same trap `splitByCategory` documents.
 */
const REFERENCE_LINK = /[ \t]*\(\[(?:#\d+|[0-9a-f]{7,40})\]\([^()\s]*\)\)/g;
const BARE_REFERENCE = /[ \t]*\(#\d+\)/g;

/**
 * A reference an author wrote mid-sentence ("reland [#2108](url)") rather than
 * one release-please appended. The surrounding prose depends on it, so the link
 * is unwrapped to its label instead of deleted.
 */
const INLINE_REFERENCE_LINK = /\[(#\d+|[0-9a-f]{7,40})\]\([^()\s]*\)/g;

/**
 * Strip the PR/commit references release-please appends to every entry, and
 * unlink any reference left mid-sentence. Every one of them points into the
 * repo, which is private — as links they 404 for most readers, and as trailing
 * noise they push the actual change off the end of the line.
 */
export function stripChangelogReferences(text: string): string {
  return text
    .replace(REFERENCE_LINK, '')
    .replace(BARE_REFERENCE, '')
    .replace(INLINE_REFERENCE_LINK, '$1')
    .trim();
}

/** Title-case a raw scope for display: `sandbox-filesystem` -> `Sandbox filesystem`. */
function prettifyScope(scope: string): string {
  const words = scope
    .replace(/^plugin-/, '')
    .split(/[-_]/)
    .filter(Boolean);
  if (words.length === 0) return CHANGELOG_GENERAL_AREA;
  const [first, ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/**
 * Resolve a commit scope to the product area it renders under. Unmapped scopes
 * become their own prettified area, and a missing scope becomes
 * {@link CHANGELOG_GENERAL_AREA}.
 */
export function changelogAreaLabel(scope: string | undefined): string {
  if (!scope) return CHANGELOG_GENERAL_AREA;
  const normalized = scope.trim().toLowerCase();
  return SCOPE_AREAS[normalized] ?? prettifyScope(normalized);
}

/** Uppercase the first letter so entries read as sentences, not commit subjects. */
function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A bullet, capturing its indent so a nested one folds instead of standing alone. */
const BULLET_LINE = /^([ \t]*)[-*][ \t]+(\S.*)$/;
const SCOPE_PREFIX = /^\*\*([^*:]+):\*\*[ \t]*/;
const THEMATIC_BREAK = /^(?:[-*_][ \t]*){3,}$/;

/**
 * Parse a changelog body (one release's markdown, with or without `###`
 * category headings) into entries. Indented sub-bullets and wrapped
 * continuation lines fold into the entry above them: a sub-bullet detailing a
 * change belongs with that change, not as a scope-less entry of its own filed
 * under a different area.
 */
export function parseChangelogEntries(markdown: string): ChangelogEntry[] {
  const raw: string[] = [];
  const append = (text: string) => {
    if (raw.length > 0) raw[raw.length - 1] = `${raw[raw.length - 1]} ${text}`;
  };
  for (const line of markdown.split('\n')) {
    const trimmed = line.trim();
    if (trimmed === '' || trimmed.startsWith('#') || THEMATIC_BREAK.test(trimmed)) continue;
    const bullet = line.match(BULLET_LINE);
    if (bullet) {
      if (bullet[1] === '') raw.push(bullet[2].trim());
      else append(bullet[2].trim());
      continue;
    }
    append(trimmed);
  }

  const entries: ChangelogEntry[] = [];
  for (const line of raw) {
    const scopeMatch = line.match(SCOPE_PREFIX);
    // A multi-scope commit (`**web,server:**`) is filed under its first scope.
    const scope = scopeMatch?.[1].split(',')[0].trim().toLowerCase() || undefined;
    const body = stripChangelogReferences(scopeMatch ? line.slice(scopeMatch[0].length) : line);
    if (body === '') continue;
    const entry: ChangelogEntry = { area: changelogAreaLabel(scope), text: sentenceCase(body) };
    if (scope) entry.scope = scope;
    entries.push(entry);
  }
  return entries;
}

function areaRank(area: string): number {
  if (area === CHANGELOG_GENERAL_AREA) return CHANGELOG_AREA_ORDER.length + 1;
  const index = (CHANGELOG_AREA_ORDER as readonly string[]).indexOf(area);
  return index === -1 ? CHANGELOG_AREA_ORDER.length : index;
}

/**
 * Group entries by product area, curated areas first (in
 * {@link CHANGELOG_AREA_ORDER}), then fallback areas alphabetically, then
 * {@link CHANGELOG_GENERAL_AREA}. Entry order within an area is preserved.
 */
export function groupChangelogEntriesByArea(entries: ChangelogEntry[]): ChangelogAreaGroup[] {
  const groups = new Map<string, ChangelogEntry[]>();
  for (const entry of entries) {
    const existing = groups.get(entry.area);
    if (existing) existing.push(entry);
    else groups.set(entry.area, [entry]);
  }
  return [...groups.entries()]
    .map(([area, grouped]) => ({ area, entries: grouped }))
    .sort((a, b) => areaRank(a.area) - areaRank(b.area) || a.area.localeCompare(b.area));
}
